import React, { useState } from 'react';
import {
  Box,
  Button,
  Text,
  Textarea,
  VStack,
  HStack,
  Alert,
  Dialog,
  Flex,
} from '@chakra-ui/react';

const SendToQueueModal = ({ agent, onClose, onConfirm }) => {
  const [queueStatus, setQueueStatus] = useState('');
  const [queueRemark, setQueueRemark] = useState('');
  const [error, setError] = useState('');

  const handleSubmit = () => {
    if (!queueStatus) {
      setError('Please select a queue status (STOP or PENDING)');
      return;
    }

    if (!queueRemark || queueRemark.trim() === '') {
      setError('Please enter a remark explaining the reason');
      return;
    }

    onConfirm({ queue_status: queueStatus, queue_remark: queueRemark.trim() });
  };

  const handleStatusChange = (status) => {
    setQueueStatus(status);
    setError('');
  };

  const handleRemarkChange = (e) => {
    setQueueRemark(e.target.value);
    setError('');
  };

  return (
    <Dialog.Root open={true} onOpenChange={(e) => !e.open && onClose()}>
      <Dialog.Backdrop bg="blackAlpha.600" />
      <Dialog.Positioner>
        <Dialog.Content maxW="md" borderRadius="xl" mx={4}>
          <Dialog.Header
            bg="orange.50"
            borderTopRadius="xl"
            borderBottom="1px"
            borderColor="gray.200"
            py={4}
            px={6}
          >
            <Flex justify="space-between" align="center" w="100%">
              <Text fontSize="lg" fontWeight="bold" color="orange.700">
                📤 Send to Pending Queue
              </Text>
            </Flex>
          </Dialog.Header>
          <Dialog.CloseTrigger />

          <Dialog.Body py={6} px={6}>
            <VStack spacing={5} align="stretch">
              {/* Agent Info Summary */}
              <Box
                bg="gray.50"
                p={4}
                borderRadius="lg"
                border="1px"
                borderColor="gray.200"
              >
                <HStack spacing={4}>
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="bold">Agent:</Text> {agent.agent_name || 'N/A'}
                  </Text>
                  <Text fontSize="sm" color="gray.600">
                    <Text as="span" fontWeight="bold">Country:</Text> {agent.country || 'N/A'}
                  </Text>
                </HStack>
              </Box>

              {/* Queue Status Selection */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={3}>
                  Queue Status *
                </Text>
                <HStack spacing={3}>
                  <Button
                    flex={1}
                    size="lg"
                    variant={queueStatus === 'STOP' ? 'solid' : 'outline'}
                    colorScheme={queueStatus === 'STOP' ? 'red' : 'gray'}
                    onClick={() => handleStatusChange('STOP')}
                    borderWidth={queueStatus === 'STOP' ? '0' : '2px'}
                    _hover={{ transform: 'scale(1.02)' }}
                    transition="all 0.2s"
                  >
                    <HStack>
                      <Text>🛑</Text>
                      <Text>STOP</Text>
                    </HStack>
                  </Button>
                  <Button
                    flex={1}
                    size="lg"
                    variant={queueStatus === 'PENDING' ? 'solid' : 'outline'}
                    colorScheme={queueStatus === 'PENDING' ? 'yellow' : 'gray'}
                    onClick={() => handleStatusChange('PENDING')}
                    borderWidth={queueStatus === 'PENDING' ? '0' : '2px'}
                    _hover={{ transform: 'scale(1.02)' }}
                    transition="all 0.2s"
                  >
                    <HStack>
                      <Text>⏳</Text>
                      <Text>PENDING</Text>
                    </HStack>
                  </Button>
                </HStack>

                {/* Status Description */}
                {queueStatus && (
                  <Box mt={3} p={3} bg={queueStatus === 'STOP' ? 'red.50' : 'yellow.50'} borderRadius="md">
                    <Text fontSize="sm" color={queueStatus === 'STOP' ? 'red.600' : 'yellow.700'}>
                      {queueStatus === 'STOP'
                        ? '⚠️ STOP: Agent operations temporarily stopped'
                        : 'ℹ️ PENDING: Agent awaiting review or update'
                      }
                    </Text>
                  </Box>
                )}
              </Box>

              {/* Remark Textarea */}
              <Box>
                <Text fontSize="sm" fontWeight="semibold" color="gray.700" mb={2}>
                  Remark *
                </Text>
                <Textarea
                  placeholder="Enter the reason for sending this agent to the queue..."
                  value={queueRemark}
                  onChange={handleRemarkChange}
                  rows={4}
                  resize="vertical"
                  borderColor="gray.300"
                  _focus={{ borderColor: 'orange.400', boxShadow: '0 0 0 1px var(--chakra-colors-orange-400)' }}
                />
                <Text fontSize="xs" color="gray.500" mt={1}>
                  Explain why this agent is being sent to the pending queue
                </Text>
              </Box>

              {/* Error Alert */}
              {error && (
                <Alert.Root status="error" borderRadius="md">
                  <Alert.Indicator />
                  <Alert.Content>
                    <Alert.Description>{error}</Alert.Description>
                  </Alert.Content>
                </Alert.Root>
              )}
            </VStack>
          </Dialog.Body>

          <Dialog.Footer
            bg="gray.50"
            borderBottomRadius="xl"
            borderTop="1px"
            borderColor="gray.200"
            px={6}
            py={4}
          >
            <HStack spacing={3} w="100%" justify="flex-end">
              <Button variant="outline" onClick={onClose}>
                Cancel
              </Button>
              <Button
                colorScheme="orange"
                onClick={handleSubmit}
                leftIcon={<Text>📤</Text>}
              >
                Send to Queue
              </Button>
            </HStack>
          </Dialog.Footer>
        </Dialog.Content>
      </Dialog.Positioner>
    </Dialog.Root>
  );
};

export default SendToQueueModal;
